import { useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Form,
  InputMask,
  ModalFooter,
  SelectSearch,
} from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalization } from "@i18n";
import { isComplexAdmin } from "@shared/api";
import { useUserContext } from "@shared/contexts";

import type { AdminUserType } from "../../types";
import {
  clampTellusEmailValue,
  createAdminUserSchema,
  AdminUserFormData,
  CreateAdminUserSubmitData,
  isTellusEmailModeForFormData,
  mapAdminUserFormToCreateRequest,
  TELLUS_EMAIL_DOMAIN_SUFFIX,
} from "../../schemas";

interface CreateAdminUserFormProps {
  onSubmit: (data: CreateAdminUserSubmitData) => void;
  onCancel: () => void;
}

export const CreateAdminUserForm = ({
  onSubmit,
  onCancel,
}: CreateAdminUserFormProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const tenantAccess = me?.tenantAccess;

  const canChooseUserType = tenantAccess?.tenantRole === "owner";
  const forceTellusEmailMode =
    isComplexAdmin(tenantAccess) && tenantAccess?.userType === "tellus";

  const schema = useMemo(
    () =>
      createAdminUserSchema(translate, {
        includeUserTypeField: canChooseUserType,
        forceTellusEmailMode,
      }),
    [translate, canChooseUserType, forceTellusEmailMode],
  );

  const form = useForm<AdminUserFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      userType: "client",
      email: "",
    },
  });

  const selectedUserType = form.watch("userType") ?? "client";
  const tellusEmailMode = isTellusEmailModeForFormData(
    { userType: selectedUserType },
    forceTellusEmailMode,
  );
  const emailValue = form.watch("email") ?? "";

  useEffect(() => {
    if (tellusEmailMode) {
      form.setValue("email", TELLUS_EMAIL_DOMAIN_SUFFIX, {
        shouldValidate: false,
      });
      form.clearErrors("email");
      return;
    }
    form.setValue("email", "", { shouldValidate: false });
    form.clearErrors("email");
  }, [form, tellusEmailMode]);

  useEffect(() => {
    if (!tellusEmailMode) {
      return;
    }
    const clamped = clampTellusEmailValue(emailValue);
    if (clamped !== emailValue) {
      form.setValue("email", clamped, { shouldValidate: false });
    }
  }, [emailValue, form, tellusEmailMode]);

  const userTypeOptions = useMemo(
    () =>
      (["client", "tellus"] as AdminUserType[]).map((value) => ({
        value,
        label: translate(`admin.users.userType.${value}`),
      })),
    [translate],
  );

  const handleFormSubmit = (data: AdminUserFormData) => {
    onSubmit(
      mapAdminUserFormToCreateRequest(data, {
        forceTellusEmailMode,
        includeUserTypeInPayload: canChooseUserType,
      }),
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} noValidate>
        <div className="px-[32px] py-[16px]">
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              label={translate("admin.users.form.label.name")}
              name="name"
              type="text"
              placeholder={translate("admin.users.form.placeholder.name")}
            />

            {canChooseUserType ? (
              <SelectSearch
                control={form.control}
                label={translate("admin.users.form.label.userType")}
                name="userType"
                options={userTypeOptions}
                placeholder={translate(
                  "admin.users.form.placeholder.userTypeSelect",
                )}
              />
            ) : null}

            <Input
              control={form.control}
              label={translate("admin.users.form.label.email")}
              name="email"
              type={tellusEmailMode ? "text" : "email"}
              placeholder={
                tellusEmailMode
                  ? translate("admin.users.form.placeholder.emailTellus")
                  : translate("admin.users.form.placeholder.email")
              }
              data-testid={tellusEmailMode ? "tellus-email-input" : undefined}
            />

            <InputMask
              control={form.control}
              label={translate("admin.users.form.label.phone")}
              name="phone"
              type="text"
              mask="(99) 99999-9999"
            />
            <Input
              control={form.control}
              label={translate("admin.users.form.label.department")}
              name="department"
              type="text"
              placeholder={translate(
                "admin.users.form.placeholder.department",
              )}
            />
            <Input
              control={form.control}
              label={translate("admin.users.form.label.position")}
              name="position"
              type="text"
              placeholder={translate("admin.users.form.placeholder.position")}
            />
          </div>
        </div>
        <ModalFooter
          footer={
            <Button type="submit" variant="primary">
              {translate("admin.users.button.save")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("admin.users.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
